import React, { useState, useEffect } from "react";
import {
  Grid,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TextField,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  Alert,
  Stack,
  Switch,
  FormControlLabel
} from "@mui/material";
import Scrollbar from "src/components/Scrollbar";
import Page from "src/components/Page";
import { getDashboardData, updateOutstanding, getInvoiceData, sendInvoiceEmail,updateEditReason, updateInvoiceApproval } from "src/api/credits";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import { PDFViewer } from '@react-pdf/renderer';
import InvoicePDF from "src/templates/InvoicePDF";
import { savePDF } from "src/templates/pdfUtils"
import { getAppStorageItem } from "src/utils/storageProxy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const APPROVAL_USER_TYPES = {
  OPS: 41,
  ACCOUNTS: 42,
  MAIN: 6
};

const getCurrentUserType = () => {
  const storedUserType = getNumericId(getAppStorageItem('user_type'));
  if (storedUserType !== null) {
    return storedUserType;
  }

  const tokenPayload = decodeJwtPayload(getAppStorageItem('access_token'));
  const tokenUserType = tokenPayload?.user_type ??
    tokenPayload?.UserType ??
    tokenPayload?.userType ??
    tokenPayload?.role ??
    tokenPayload?.Role;

  return getNumericId(tokenUserType);
};

const MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const getNumericId = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const decodeJwtPayload = (token) => {
  if (!token || typeof atob !== 'function') {
    return null;
  }

  try {
    const payload = String(token).split('.')[1];
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '='
    );

    return JSON.parse(atob(paddedPayload));
  } catch (error) {
    return null;
  }
};

const getCurrentUserId = () => {
  const storedUserId = getNumericId(getAppStorageItem('ID'));
  if (storedUserId !== null) {
    return storedUserId;
  }

  const tokenPayload = decodeJwtPayload(getAppStorageItem('access_token'));
  const tokenUserId = tokenPayload?.ID ??
    tokenPayload?.Id ??
    tokenPayload?.id ??
    tokenPayload?.UserId ??
    tokenPayload?.userId ??
    tokenPayload?.user_id ??
    tokenPayload?.sub ??
    tokenPayload?.nameid ??
    tokenPayload?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];

  return getNumericId(tokenUserId);
};

const ApprovalSwitchControl = React.memo(
  ({ label, checked, disabled, onChange }) => (
    <FormControlLabel
      sx={{
        m: 0,
        pointerEvents: 'auto',
        '& .MuiFormControlLabel-label': {
          fontSize: 12,
          lineHeight: 1.2,
          whiteSpace: 'nowrap'
        }
      }}
      control={(
        <Switch
          size="small"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
        />
      )}
      label={label}
    />
  ),
  (prevProps, nextProps) => (
    prevProps.label === nextProps.label &&
    prevProps.checked === nextProps.checked &&
    prevProps.disabled === nextProps.disabled
  )
);

export default function BillingDashboard() {
  const FIREFOX_PDF_TOOLBAR_HEIGHT = 32;
  const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
  const [yearError, setYearError] = useState('');
  const [monthsError, setMonthsError] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [dashboardData, setDashboardData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  //const [editCreditValue, setEditCreditValue] = useState('');
  const [editAdditionalAmount, setEditAdditionalAmount] = useState('');
  const [editAdditionalAmountType, setEditAdditionalAmountType] = useState('');
  const [loading, setLoading] = useState(false);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [currentInvoiceData, setCurrentInvoiceData] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [errorStatus,setErrorStatus] = useState(false)
  const [currentClientName, setCurrentClientName] = useState('');
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [updateReason, setUpdateReason] = useState('');
  const [sendEmailContext, setSendEmailContext] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [approvalLoadingId, setApprovalLoadingId] = useState(null);
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1].filter(year => year !== 2025);
  const [message, setMessage] = useState('');
  const [status, setStatusBase] = useState(false);
  const [alertType, setAlertType] = useState('success');
  const currentUserType = getCurrentUserType();

  const monthOptions = MONTH_OPTIONS;

  const asArray = (value) => {
    if (Array.isArray(value)) {
      return value;
    }

    return value === null || value === undefined || value === '' ? [] : [value];
  };

  const getValueAt = (value, index) => {
    const values = asArray(value);

    if (values.length === 0) {
      return undefined;
    }

    return values[index] !== undefined ? values[index] : values[0];
  };

  const getOptionalUpdateValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    const valueString = String(value).trim();
    return valueString === '' ? null : valueString;
  };

  const setValueAt = (value, index, nextValue) => {
    const values = asArray(value);
    const updateIndex = Math.max(index, 0);
    const nextValues = values.length > 0 ? [...values] : [];

    nextValues[updateIndex] = nextValue;

    return nextValues;
  };

  const getLocationValues = (value) => {
  // Treat locations as atomic - never split by comma
  const values = asArray(value);
  return values.filter(Boolean);
};

  const hasValue = (value) => value !== null && value !== undefined && value !== '';

  // const getIndexedValues = (value) => (
  //   asArray(value).flatMap((item) => {
  //     if (typeof item === 'string') {
  //       return item.split(',').map((part) => part.trim());
  //     }

  //     return [item];
  //   })
  // );

  const getIndexedValues = (value) => (
  asArray(value).flatMap((item) => {
    if (typeof item === 'string' && item.includes(',')) {
      // Only split if it actually contains commas
      return item.split(',').map((part) => part.trim());
    }
    return [item];
  })
);

const approvalStatusChipColors = {
  activeReviewed: {
    backgroundColor: '#d7fbe9',
    borderColor: '#8cefc1',
    color: '#007f63',
    hoverBackgroundColor: '#c9f7df',
    hoverBorderColor: '#78e7b5'
  },
  disabledReviewed: {
    backgroundColor: '#f1f5f9',
    borderColor: '#e2e8f0',
    color: '#7488a3',
    hoverBackgroundColor: '#e9eff6',
    hoverBorderColor: '#d7e1ec'
  },
  pending: {
    backgroundColor: '#ffffff',
    borderColor: '#d8e2ee',
    color: '#9aabc1',
    hoverBackgroundColor: '#f8fafc',
    hoverBorderColor: '#cbd8e8'
  }
};

const renderApprovalStatusChip = ({
  approved,
  disabled,
  onClick,
  reviewedVariant = 'disabledReviewed'
}) => {
  const statusText = approved ? 'Reviewed' : 'Pending';
  const statusColors = approvalStatusChipColors[approved ? reviewedVariant : 'pending'];
  const StatusIcon = approved ? CheckIcon : CloseIcon;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minWidth: 0,
        pointerEvents: 'auto'
      }}
    >
      <Button
        type="button"
        size="small"
        disableElevation
        disableRipple
        disableFocusRipple
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : onClick}
        sx={{
          minWidth: 0,
          height: 24,
          px: 1.1,
          py: 0,
          gap: 0.5,
          borderRadius: '999px',
          border: `1px solid ${statusColors.borderColor}`,
          backgroundColor: statusColors.backgroundColor,
          color: statusColors.color,
          textTransform: 'none',
          fontSize: '0.75rem',
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: 0,
          boxShadow: 'none',
          whiteSpace: 'nowrap',
          cursor: disabled ? 'default' : 'pointer',
          '&:hover': {
            backgroundColor: disabled ? statusColors.backgroundColor : statusColors.hoverBackgroundColor,
            borderColor: disabled ? statusColors.borderColor : statusColors.hoverBorderColor,
            boxShadow: 'none'
          },
          '& .MuiButton-startIcon': {
            m: 0
          }
        }}
        startIcon={(
          <Box
            component="span"
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: `1.5px solid ${statusColors.color}`,
              color: statusColors.color,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <StatusIcon sx={{ fontSize: approved ? 9 : 8, strokeWidth: 1.8 }} />
          </Box>
        )}
      >
        {statusText}
      </Button>
    </Box>
  );
};

const renderOpsApprovalControl = (actionTarget) => {
  if (!actionTarget) {
    return null;
  }

  const isOpsApproved = toApprovalFlag(actionTarget.isOpsApproved) === 1;
  const isBillingIdMissing = !hasValue(actionTarget.billingId);
  const isOpsApprovalUpdating = approvalLoadingId === getApprovalLoadingKey(actionTarget.billingId, 'IsOpsApproved');

  const isMainUser = currentUserType === APPROVAL_USER_TYPES.MAIN;
  const isOpsUser = currentUserType === APPROVAL_USER_TYPES.OPS;

  const canEditOps = isOpsUser;
  const isOpsDisabled = isMainUser || !canEditOps || isBillingIdMissing || isOpsApprovalUpdating;

  const handleOpsClick = () => {
    if (!isOpsDisabled && canEditOps) {
      handleApprovalChange(actionTarget, 'IsOpsApproved', !isOpsApproved);
    }
  };

  return renderApprovalStatusChip({
    approved: isOpsApproved,
    disabled: isOpsDisabled,
    onClick: handleOpsClick,
    reviewedVariant: isOpsUser ? 'activeReviewed' : 'disabledReviewed'
  });
};

const renderAccountsApprovalControl = (actionTarget) => {
  if (!actionTarget) {
    return null;
  }

  const isAccountsApproved = toApprovalFlag(actionTarget.isAccountsApproved) === 1;
  const isBillingIdMissing = !hasValue(actionTarget.billingId);
  const isAccountsApprovalUpdating = approvalLoadingId === getApprovalLoadingKey(actionTarget.billingId, 'IsAccountsApproved');

  const isMainUser = currentUserType === APPROVAL_USER_TYPES.MAIN;
  const isAccountsUser = currentUserType === APPROVAL_USER_TYPES.ACCOUNTS;
  const isOpsUser = currentUserType === APPROVAL_USER_TYPES.OPS;

  const canEditAccounts = isAccountsUser;
  const isAccountsDisabled = isMainUser || !canEditAccounts || isBillingIdMissing || isAccountsApprovalUpdating || (isOpsUser && !toApprovalFlag(actionTarget.isOpsApproved));

  const handleAccountsClick = () => {
    if (!isAccountsDisabled && canEditAccounts) {
      handleApprovalChange(actionTarget, 'IsAccountsApproved', !isAccountsApproved);
    }
  };

  return renderApprovalStatusChip({
    approved: isAccountsApproved,
    disabled: isAccountsDisabled,
    onClick: handleAccountsClick,
    reviewedVariant: isAccountsUser ? 'activeReviewed' : 'disabledReviewed'
  });
};

  const getIndexedValueFromList = (value, index) => {
    
    const values = getIndexedValues(value);

    if (values.length === 0) {
      return undefined;
    }

    if (hasValue(values[index])) {
      return values[index];
    }

    return values.length === 1 ? values[0] : undefined;
  };

  const getInvoiceIdAt = (rowData, index) => getIndexedValueFromList(rowData.invoice_service_id, index);
  const getBillingIdAt = (rowData, index) => getIndexedValueFromList(rowData.billing_id, index);
  const getInvoiceDatabaseIdAt = (rowData, index) => getIndexedValueFromList(rowData.invoice_id, index);
  const getApprovalFlagAt = (rowData, fieldName, index) => (
    Number(getIndexedValueFromList(rowData[fieldName], index)) === 1 ? 1 : 0
  );

  const toApprovalFlag = (value) => (Number(value) === 1 ? 1 : 0);

  const hasPositiveId = (value) => {
    if (!hasValue(value)) {
      return false;
    }

    const numericValue = Number(value);
    return !Number.isNaN(numericValue) && numericValue > 0;
  };

  const isInvoiceActionEnabled = (actionTarget) => (
    //hasPositiveId(actionTarget?.invoiceDatabaseId) &&
    toApprovalFlag(actionTarget?.isOpsApproved) === 1 &&
    toApprovalFlag(actionTarget?.isAccountsApproved) === 1
  );

  const getUniqueValues = (values) => {
    const seen = new Set();

    return values.filter((value) => {
      if (!hasValue(value)) {
        return false;
      }

      const key = String(value).trim().toLowerCase();
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  };

  const getFirstValueForGroup = (sourceValues, sourceIndices, preferNonZero = false) => {
    const groupValues = sourceIndices
      .map((sourceIndex) => sourceValues[sourceIndex])
      .filter(hasValue);

    if (preferNonZero) {
      const nonZeroValue = groupValues.find((value) => String(value).trim() !== '0');
      if (hasValue(nonZeroValue)) {
        return nonZeroValue;
      }
    }

    return groupValues.length > 0 ? groupValues[0] : '';
  };

  const getValuesForGroup = (sourceValues, sourceIndices) => (
    sourceIndices
      .map((sourceIndex) => sourceValues[sourceIndex])
      .filter(hasValue)
  );

  const getGroupedDisplayValue = (sourceValues, sourceIndices) => {
    const groupValues = getValuesForGroup(sourceValues, sourceIndices);

    if (groupValues.length === 0) {
      return '';
    }

    return groupValues.join('\n');
  };

  const getIndexedValue = (sourceValues, index) => {
    if (sourceValues.length === 0) {
      return undefined;
    }

    if (hasValue(sourceValues[index])) {
      return sourceValues[index];
    }

    return sourceValues.length === 1 ? sourceValues[0] : undefined;
  };

  const getServiceLineCount = (item) => {
    const fields = [
      'ClientId',
      'displayname',
      'PricingType',
      'ViewsConsidered',
      'total_pricing',
      'StorageAmount',
      'invoice_service_id'
    ];
    const locationLineCount = getLocationValues(item.Location).length;

    return Math.max(
      1,
      locationLineCount,
      ...fields.map((fieldName) => getIndexedValues(item[fieldName]).length)
    );
  };

  const buildBillingLineGroups = (item, serviceLineCount) => {
    const clientIds = getIndexedValues(item.ClientId);
    const displayNames = getIndexedValues(item.displayname);
    const locations = getLocationIndexedValues(item.Location);
    const groupedLines = [];
    const groupedLineMap = new Map();

    Array.from({ length: serviceLineCount }, (_, sourceIndex) => sourceIndex).forEach((sourceIndex) => {
      const clientId = getIndexedValue(clientIds, sourceIndex);
      const displayname = getIndexedValue(displayNames, sourceIndex);
      const location = getIndexedValue(locations, sourceIndex);
      const keyParts = [clientId, displayname, location]
        .filter(hasValue)
        .map((value) => String(value).trim().toLowerCase());
      const lineKey = keyParts.length > 0 ? keyParts.join('|') : `line-${sourceIndex}`;

      if (!groupedLineMap.has(lineKey)) {
        const groupedLine = {
          sourceIndices: []
        };

        groupedLineMap.set(lineKey, groupedLine);
        groupedLines.push(groupedLine);
      }

      groupedLineMap.get(lineKey).sourceIndices.push(sourceIndex);
    });

    return groupedLines;
  };

  const getGroupedFieldValues = (item, fieldName, groupedLines, serviceLineCount) => {
    const values = getIndexedValues(item[fieldName]);

    if (values.length === 0) {
      return groupedLines.map(() => '');
    }

    if (values.length === groupedLines.length) {
      return values;
    }

    if (values.length === serviceLineCount) {
      return groupedLines.map((group) =>
        getFirstValueForGroup(values, group.sourceIndices)
      );
    }

    return groupedLines.map((_, index) => (hasValue(values[index]) ? values[index] : ''));
  };

  // Add this function specifically for locations
const getLocationIndexedValues = (value) => {
  const values = asArray(value);
  // Return as-is, never split locations
  return values.filter(Boolean);
};

  const getDashboardResponseEntry = (item) => {
    if (!item || Array.isArray(item) || typeof item !== 'object') {
      return null;
    }

    if (item.ClientId !== undefined || item.displayname !== undefined) {
      return {
        clientName: item.displayname || item.ClientName || item.clientName || '',
        data: item,
        isKeyedResponse: false
      };
    }

    const entryKey = Object.keys(item).find((key) => {
      const value = item[key];
      return value && typeof value === 'object' && !Array.isArray(value);
    });

    if (!entryKey) {
      return null;
    }

    return {
      clientName: entryKey,
      data: item[entryKey],
      isKeyedResponse: true
    };
  };

  const getDashboardGroupName = (value) => (
    String(value || '').trim().replace(/\s+/g, ' ').toLowerCase()
  );

  const getNumericTotal = (items, fieldName) => (
    asArray(items).reduce((total, item) => {
      const numericValue = Number(
        item && typeof item === 'object'
          ? item[fieldName] ?? 0
          : item
      );
      return Number.isFinite(numericValue) ? total + numericValue : total;
    }, 0)
  );

  const getPositiveIdValue = (value) => {
    if (!hasValue(value)) {
      return '';
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? value : '';
  };

  const getFirstPositiveValue = (values) => (
    values.map(getPositiveIdValue).find(hasValue) || ''
  );

  const getOutstandingDetails = (entryData) => asArray(entryData?.OutstandingAmounts);

  const getOutstandingAmount = (entryData) => {
    const details = getOutstandingDetails(entryData);

    if (details.length > 0) {
      return getNumericTotal(details, 'OutstandingAmount');
    }

    return Number(entryData?.OutstandingAmount ?? 0) || 0;
  };

  const getInvoiceServiceId = (entryData) => {
    const outstandingInvoiceServiceId = getFirstPositiveValue(
      getOutstandingDetails(entryData).map((detail) => detail?.InvoiceServiceId)
    );

    if (hasValue(outstandingInvoiceServiceId)) {
      return outstandingInvoiceServiceId;
    }

    return getFirstPositiveValue(getIndexedValues(entryData?.invoice_service_id));
  };

  const getCreditAmount = (entryData) => getNumericTotal(entryData?.Credits, 'Credit');

  const getAdditionalAmount = (entryData) => getNumericTotal(entryData?.AdditionalAmounts, 'AdditionalAmount');

  const getAdditionalAmountType = (entryData) => (
    asArray(entryData?.AdditionalAmounts)
      .map((item) => item?.AdditionalAmountType)
      .filter(hasValue)
      .join('\n')
  );

  const normalizeDashboardEntry = (item, index) => {
    const entry = getDashboardResponseEntry(item);

    if (!entry) {
      return {
        row: item,
        groupKey: `row-${index}`
      };
    }

    const { clientName, data } = entry;

    if (!entry.isKeyedResponse) {
      return {
        row: data,
        groupKey: `row-${index}`
      };
    }

    const row = {
      ...data,
      displayname: data.displayname ?? clientName,
      ClientId: data.ClientId,
      Location: data.Location ?? '',
      Credits: getCreditAmount(data),
      OutstandingAmount: getOutstandingAmount(data),
      AdditionalAmount: getAdditionalAmount(data),
      AdditionalAmountType: getAdditionalAmountType(data),
      invoice_service_id: getInvoiceServiceId(data),
      invoice_id: getPositiveIdValue(data.invoice_id),
      invoice_no: data.invoice_no ?? '',
      billing_id: data.billing_id,
      IsOpsApproved: data.IsOpsApproved,
      IsAccountsApproved: data.IsAccountsApproved
    };

    const groupKey = hasValue(row.billing_id)
      ? `billing-${row.billing_id}`
      : hasValue(row.invoice_id)
        ? `invoice-${row.invoice_id}`
        : `row-${getDashboardGroupName(row.displayname)}-${row.ClientId || index}`;

    return {
      row,
      groupKey
    };
  };

  const mergeDashboardEntries = (entries, groupIndex) => {
    if (entries.length === 1) {
      return entries[0].row;
    }

    const fieldsToGroup = [
      'ClientId',
      'displayname',
      'Location',
      'no_of_cameras',
      'no_of_views',
      'ViewsConsidered',
      'total_pricing',
      'Credits',
      'OutstandingAmount',
      'AdditionalAmount',
      'AdditionalAmountType',
      'invoice_service_id',
      'invoice_id',
      'invoice_no',
      'billing_id',
      'IsOpsApproved',
      'IsAccountsApproved',
      'InvoiceEmails',
      'BillingAddress',
      'StorageAmount',
      'frequency',
      'time'
    ];

    const mergedRow = {
      ...entries[0].row,
      uniqueId: `billing-group-${groupIndex}`
    };

    fieldsToGroup.forEach((fieldName) => {
      mergedRow[fieldName] = entries.map(({ row }) => (
        row[fieldName] !== undefined && row[fieldName] !== null ? row[fieldName] : ''
      ));
    });

    return mergedRow;
  };

  const normalizeDashboardRows = (responseData) => {
    const entries = asArray(responseData).map(normalizeDashboardEntry);
    const groups = new Map();

    entries.forEach((entry) => {
      if (!groups.has(entry.groupKey)) {
        groups.set(entry.groupKey, []);
      }

      groups.get(entry.groupKey).push(entry);
    });

    return Array.from(groups.values()).map((groupEntries, index) =>
      normalizeDashboardRow(mergeDashboardEntries(groupEntries, index), index)
    );
  };

  const normalizeDashboardRow = (item, index) => {
    
	    const normalizedRow = {
	      ...item,
	      uniqueId: item.uniqueId || item.id || item.clientId || `row-${index}-${Date.now()}`
	    };

    const serviceLineCount = getServiceLineCount(item);
    const groupedLines = buildBillingLineGroups(item, serviceLineCount);
    const groupDisplayFields = ['ViewsConsidered', 'total_pricing', 'StorageAmount'];
    const groupFirstValueFields = ['ClientId', 'displayname'];
    const groupIndexedFields = [
      'no_of_cameras',
      'no_of_views',
      'Credits',
      'OutstandingAmount',
      //'AdditionalAmount',
      //'AdditionalAmountType'
    ];

    normalizedRow._billingLineCount = groupedLines.length;
    normalizedRow._billingLineSourceIndices = groupedLines.map((group) => group.sourceIndices);

    groupFirstValueFields.forEach((fieldName) => {
      if (item[fieldName] !== undefined) {
        const sourceValues = getIndexedValues(item[fieldName]);
        normalizedRow[fieldName] = groupedLines.map((group) =>
          getFirstValueForGroup(sourceValues, group.sourceIndices)
        );
      }
    });

     if (item.Location !== undefined) {
    const sourceValues = getLocationIndexedValues(item.Location); // Use the new function
    normalizedRow.Location = groupedLines.map((group) =>
      getFirstValueForGroup(sourceValues, group.sourceIndices)
    );
  }

    groupDisplayFields.forEach((fieldName) => {
      if (item[fieldName] !== undefined) {
        const sourceValues = getIndexedValues(item[fieldName]);
        normalizedRow[fieldName] = groupedLines.map((group) =>
          getGroupedDisplayValue(sourceValues, group.sourceIndices)
        );
      }
    });

    normalizedRow.PricingType = groupedLines.map((group) => (
      getUniqueValues(getValuesForGroup(getIndexedValues(item.PricingType), group.sourceIndices)).join('\n')
    ));

    groupIndexedFields.forEach((fieldName) => {
      if (item[fieldName] !== undefined) {
        normalizedRow[fieldName] = getGroupedFieldValues(item, fieldName, groupedLines, serviceLineCount);
      }
    });

    if (item.invoice_service_id !== undefined) {
      const invoiceServiceIds = getIndexedValues(item.invoice_service_id);
      normalizedRow.invoice_service_id = invoiceServiceIds.length === serviceLineCount
        ? groupedLines.map((group) =>
          getFirstValueForGroup(invoiceServiceIds, group.sourceIndices, true)
        )
        : getGroupedFieldValues(item, 'invoice_service_id', groupedLines, serviceLineCount);
    }

    normalizedRow._billingLineHeights = groupedLines.map((group) => (
      Math.max(
        1,
        ...groupDisplayFields.map((fieldName) =>
          getValuesForGroup(getIndexedValues(item[fieldName]), group.sourceIndices).length
        )
      )
    ));

    return normalizedRow;
  };

  const getEditTargets = (rowData, rowIndex) => {
  // Use _billingLineCount as the primary source of truth
  const billingLineCount = rowData._billingLineCount || 0;

  // Don't use locations.length to determine row count
  const visibleLineCount = billingLineCount;

  const editCount = visibleLineCount <= 1 ? 1 : visibleLineCount;

  return Array.from({ length: editCount }, (_, index) => {
    const valueIndex = editCount === 1 ? 0 : index;
    const invoiceId = getInvoiceIdAt(rowData, valueIndex);

    return {
      uniqueId: `${rowIndex}-${valueIndex}`,
      valueIndex,
      clientId: getValueAt(rowData.ClientId, valueIndex),
      displayname: getValueAt(rowData.displayname, valueIndex),
      OutstandingAmount: getValueAt(rowData.OutstandingAmount, valueIndex),
      Credits: getValueAt(rowData.Credits, valueIndex),
      //AdditionalAmount: getValueAt(rowData.AdditionalAmount, valueIndex),
      //AdditionalAmountType: getValueAt(rowData.AdditionalAmountType, valueIndex),
      invoiceId,
      billingId: getBillingIdAt(rowData, valueIndex),
      invoiceDatabaseId: getInvoiceDatabaseIdAt(rowData, valueIndex),
      isOpsApproved: getApprovalFlagAt(rowData, 'IsOpsApproved', valueIndex),
      isAccountsApproved: getApprovalFlagAt(rowData, 'IsAccountsApproved', valueIndex),
      hasInvoiceId: hasValue(invoiceId)
    };
  });
};

  const innerRowLineColor = 'rgba(145, 158, 171, 0.24)';
  const mainRowLineColor = 'rgba(145, 158, 171, 0.72)';
  const serialColumnWidth = 'clamp(42px, 5vw, 70px)';
//   const billingGridTemplateColumns = [
//   'minmax(150px, 1.2fr)', // Client
//   'minmax(120px, 1fr)',   // Location
//   'minmax(80px, 0.7fr)',
//   'minmax(80px, 0.7fr)',
//   'minmax(140px, 1fr)',
//   'minmax(90px, 0.7fr)',
//   'minmax(90px, 0.7fr)',
//   'minmax(100px, 0.8fr)',
//   `minmax(${billingActionColumnWidth}px, 1.4fr)`    // Action
// ].join(' ');

const billingGridTemplateColumns = [
  'minmax(0, 1.25fr)', // Client
  'minmax(0, 1fr)',    // Location
  'minmax(0, 0.72fr)', // No of Cameras
  'minmax(0, 0.72fr)', // No of Views
  'minmax(0, 1fr)',    // Views Considered For Billing
  'minmax(0, 0.78fr)', // Total Price($)
  'minmax(0, 0.72fr)', // Storage
  'minmax(0, 0.72fr)', // Credits($)
  'minmax(124px, 1.2fr)', // Previous Balance Due($)
  'minmax(0, 0.7fr)',  // Edit
  'minmax(0, 0.62fr)', // Preview
  'minmax(0, 0.62fr)', // Share
  'minmax(0, 0.82fr)', // Ops Status
  'minmax(0, 0.92fr)'  // Accounts Status
].join(' ');
const grandTotalGridTemplateColumns = `${serialColumnWidth} ${billingGridTemplateColumns}`;
  const billingLineMinHeight = 67;
  const billingStackedValueLineHeight = 22;
  const billingStackedValueVerticalPadding = 18;
  

  const billingTableRowSx = {
    '& > td': {
      borderBottom: `1px solid ${mainRowLineColor}`,
      verticalAlign: 'middle'
    }
  };

  // const getStackedValues = (rowData, fieldName) => {
  //   const values = asArray(rowData[fieldName]);
  //   const lineCount = rowData._billingLineCount || values.length;

  //   if (lineCount === 0) {
  //     return values;
  //   }

  //   return Array.from({ length: lineCount }, (_, index) => (
  //     hasValue(values[index]) ? values[index] : ''
  //   ));
  // };

  const getStackedValues = (rowData, fieldName) => {
  const values = fieldName === 'Location'
    ? getLocationValues(rowData[fieldName])
    : asArray(rowData[fieldName]);
  const lineCount = rowData._billingLineCount || values.length;

  if (lineCount === 0) {
    return values;
  }

  return Array.from({ length: lineCount }, (_, index) => (
    hasValue(values[index]) ? values[index] : ''
  ));
};

  const displayCellValue = (value) => (hasValue(value) ? value : '');

  const getNumericValues = (value) => {
    if (Array.isArray(value)) {
      return value.flatMap(getNumericValues);
    }

    if (!hasValue(value)) {
      return [];
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? [value] : [];
    }

    return String(value)
      .split(/\r?\n/)
      .flatMap((lineValue) => {
        const normalizedValue = lineValue.trim();

        if (!normalizedValue) {
          return [];
        }

        const directValue = Number(normalizedValue.replace(/[$,\s]/g, ''));

        if (Number.isFinite(directValue)) {
          return [directValue];
        }

        const numberMatches = normalizedValue.match(/-?\d+(?:,\d{3})*(?:\.\d+)?/g);

        return numberMatches
          ? numberMatches
            .map((numberText) => Number(numberText.replace(/,/g, '')))
            .filter(Number.isFinite)
          : [];
      });
  };

  const getGrandTotalForField = (fieldName) => (
    dashboardData.reduce((fieldTotal, rowData) => (
      fieldTotal + getStackedValues(rowData, fieldName).reduce((rowTotal, value) => (
        rowTotal + getNumericValues(value).reduce((valueTotal, numericValue) => valueTotal + numericValue, 0)
      ), 0)
    ), 0)
  );

  const formatGrandTotalValue = (value) => {
    const roundedValue = Math.round((value + Number.EPSILON) * 100) / 100;

    return Number.isInteger(roundedValue) ? roundedValue : roundedValue.toFixed(2);
  };

  const grandTotals = {
    cameras: getGrandTotalForField('no_of_cameras'),
    views: getGrandTotalForField('no_of_views'),
    viewsConsidered: getGrandTotalForField('ViewsConsidered'),
    totalPricing: getGrandTotalForField('total_pricing'),
    storage: getGrandTotalForField('StorageAmount'),
    credits: getGrandTotalForField('Credits'),
    outstanding: getGrandTotalForField('OutstandingAmount')
  };

  const getGrandTotalCellSx = (align = 'center') => ({
    ...getBillingLineCellSx(align),
    minHeight: 67,
    fontWeight: 700
  });

  const getBillingLineRowSx = (rowData, index, lineCount) => {
    const stackedValueLineCount = Math.max(rowData._billingLineHeights?.[index] || 1, 1);
    const rowMinHeight = stackedValueLineCount === 1
      ? billingLineMinHeight
      : Math.max(
        billingLineMinHeight,
        (stackedValueLineCount * billingStackedValueLineHeight) + billingStackedValueVerticalPadding
      );

    return {
      display: 'grid',
      gridTemplateColumns: billingGridTemplateColumns,
      width: '100%',
      minHeight: `${rowMinHeight}px`,
      boxSizing: 'border-box',
      position: 'relative',

      ...(index < lineCount - 1 && {
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '1px',
          backgroundColor: innerRowLineColor
        }
      })
    };
  };

  const getBillingLineCellSx = (align = 'center') => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: align === 'center' ? 'center' : 'flex-start',
    minWidth: 0,
    px: 0.5,
    py: 0.75,
    textAlign: align,
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word'
  });

  const getBillingHeaderCellSx = (align = 'center') => ({
    ...getBillingLineCellSx(align),
    fontWeight: 600,
    color: 'text.secondary',
    whiteSpace: 'normal'
  });

  const previousBalanceHeaderSx = {
    ...getBillingHeaderCellSx(),
    flexDirection: 'column',
    lineHeight: 1.2,
    overflowWrap: 'normal',
    wordBreak: 'normal',
    '& > span': {
      whiteSpace: 'nowrap'
    }
  };

  const getGroupedActionCellSx = (gridColumn, align = 'center') => ({
    gridColumn,
    gridRow: '1 / 2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: align === 'left' ? 'flex-start' : 'center',
    minWidth: 0,
    px: 0.5,
    py: 0.75,
    zIndex: 1,
    backgroundColor: 'background.paper',
    pointerEvents: 'none'
  });

  useEffect(() => {
    console.log('selectedYear,selectedMonths', selectedYear, selectedMonth)
    if (selectedYear && selectedMonth) {
      const year = selectedYear;
      const monthIndex = monthOptions.findIndex(m => m === selectedMonth) + 1;
      const month = monthIndex;

      const data = { month, year }

      getDashboardData(data, (response) => {
        console.log(response)
	        if (response.status === 200) {
	          if (response.data.length > 0) {
	            const dataWithIds = normalizeDashboardRows(response.data);

	            setDashboardData(dataWithIds);
	          } else {
            setDashboardData([]);
          }
        }
      });
    }
  }, [selectedYear, selectedMonth]);

  const handleEdit = (row) => {
    
    console.log('Editing row:', row);
    const rowId = row.uniqueId; 
    setEditingId(rowId);
    setEditValue(row.OutstandingAmount || 0);
    //setEditCreditValue(row.Credits || 0);
    //setEditAdditionalAmount(row.AdditionalAmount ?? '');
    setEditAdditionalAmountType(row.AdditionalAmountType ?? '');
  }

  const handleSave = (clientData) => {
    
    setLoading(true);
    const monthIndex = monthOptions.findIndex(m => m === selectedMonth) + 1;
    const hasInvoiceId = hasValue(clientData.invoiceId);
    const data = {
      ClientId: clientData.clientId,
      Year: selectedYear,
      Month: monthIndex,
      //AdditionalAmount: getOptionalUpdateValue(editAdditionalAmount),
      //AdditionalAmountType: getOptionalUpdateValue(editAdditionalAmountType)
    }
    if (hasInvoiceId) {
      data.OutstandingAmount = editValue;
      data.InvoiceId = clientData.invoiceId;
      //data.Credits = parseInt(editCreditValue, 10);
    } else {
      data.OutstandingAmount = editValue;
    }

     
    
    updateOutstanding(data, (response) => {
      if (response.status === 200) {
        // Update the specific client's outstanding amount in the dashboardData
        setDashboardData(prevData =>
          prevData.map((row, rowIdx) => {
            if (rowIdx === clientData.rowIndex) {
              const newOutstandingAmount = [...asArray(row.OutstandingAmount)];
              const newCredits = [...asArray(row.Credits)];
              const rowClientIds = asArray(row.ClientId);
              const fallbackClientIndex = rowClientIds.findIndex(id => id === clientData.clientId);
              const clientIndex = Number.isInteger(clientData.valueIndex)
                ? clientData.valueIndex
                : fallbackClientIndex;
              const updateIndex = clientIndex >= 0 && clientIndex < newOutstandingAmount.length
                ? clientIndex
                : 0;

              if (newOutstandingAmount.length > 0) {
                newOutstandingAmount[updateIndex] = editValue;
                if (hasInvoiceId && newCredits.length > 0) {
                  const creditIndex = updateIndex < newCredits.length ? updateIndex : 0;
                  //newCredits[creditIndex] = editCreditValue;
                }
              }
              return {
                ...row,
                OutstandingAmount: newOutstandingAmount,
                Credits: newCredits.length > 0 ? newCredits : row.Credits,
                //AdditionalAmount: setValueAt(row.AdditionalAmount, updateIndex, data.AdditionalAmount),
                //AdditionalAmountType: setValueAt(row.AdditionalAmountType, updateIndex, data.AdditionalAmountType)
              };
            }
            return row;
          })
        );
        setEditingId(null);
        setEditValue('');
        //setEditCreditValue('');
        setEditAdditionalAmount('');
        setEditAdditionalAmountType('');
      }
      setLoading(false);
    });
  }

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
    //setEditCreditValue('');
    setEditAdditionalAmount('');
    setEditAdditionalAmountType('');
  }

  const getApprovalLoadingKey = (billingId, approvalField) => `${billingId}-${approvalField}`;

  const hasMatchingBillingId = (rowBillingId, billingId) => (
    getIndexedValues(rowBillingId).some((value) => String(value) === String(billingId))
  );

  const updateApprovalValueShape = (currentValue, nextValue) => (
    Array.isArray(currentValue) ? currentValue.map(() => nextValue) : nextValue
  );

	  const updateApprovalStateForRow = (billingId, approvalData) => {
	    const nextApprovalData = Array.isArray(approvalData) ? approvalData[0] : approvalData;

	    setDashboardData((prevData) =>
	      prevData.map((row) => {
	        if (!hasMatchingBillingId(row.billing_id, billingId)) {
          return row;
        }

	        return {
	          ...row,
	          IsOpsApproved: updateApprovalValueShape(row.IsOpsApproved, nextApprovalData.IsOpsApproved),
	          IsAccountsApproved: updateApprovalValueShape(row.IsAccountsApproved, nextApprovalData.IsAccountsApproved)
	        };
	      })
	    );
  };

  const handleApprovalChange = (actionTarget, approvalField, checked) => {
  if (!hasValue(actionTarget?.billingId)) {
    return;
  }

  // MAIN users cannot update any approvals
  if (currentUserType === APPROVAL_USER_TYPES.MAIN) {
    return;
  }

  // Only OPS can update OPS status
  if (approvalField === 'IsOpsApproved' && currentUserType !== APPROVAL_USER_TYPES.OPS) {
    return;
  }

  // Only ACCOUNTS can update ACCOUNTS status, and only if OPS is already approved
  if (
    approvalField === 'IsAccountsApproved' &&
    (currentUserType !== APPROVAL_USER_TYPES.ACCOUNTS || toApprovalFlag(actionTarget.isOpsApproved) !== 1)
  ) {
    return;
  }

  const nextApprovalValue = checked ? 1 : 0;
  const billingIdNumber = Number(actionTarget.billingId);
  const monthIndex = monthOptions.findIndex(m => m === selectedMonth) + 1;

  const approvalData = 
  [
    {
    BillingId: Number.isNaN(billingIdNumber) ? actionTarget.billingId : billingIdNumber,
    Month: monthIndex,
    Year: Number(selectedYear),
    IsOpsApproved: approvalField === 'IsOpsApproved'
      ? nextApprovalValue
      : toApprovalFlag(actionTarget.isOpsApproved),
    IsAccountsApproved: approvalField === 'IsAccountsApproved'
      ? nextApprovalValue
      : toApprovalFlag(actionTarget.isAccountsApproved)
  }
];
  const loadingKey = getApprovalLoadingKey(actionTarget.billingId, approvalField);

  setApprovalLoadingId(loadingKey);
  updateInvoiceApproval(approvalData, (response) => {
    if (response.status >= 200 && response.status < 300) {
      updateApprovalStateForRow(actionTarget.billingId, approvalData);
      setMessage('Approval updated successfully!');
      setAlertType('success');
      setStatusBase(true);
      setApprovalLoadingId(null);

      const year = selectedYear;
      const monthIndex = monthOptions.findIndex(m => m === selectedMonth) + 1;
      const month = monthIndex;

      const data = { month, year }

      getDashboardData(data, (response) => {
        console.log(response)
	        if (response.status === 200) {
	          if (response.data.length > 0) {
	            const dataWithIds = normalizeDashboardRows(response.data);

	            setDashboardData(dataWithIds);
	          } else {
            setDashboardData([]);
          }
        }
      });


      return;
    }

    setMessage('Failed to update approval');
    setAlertType('error');
    setStatusBase(true);
    setApprovalLoadingId(null);
  });
};

  const renderStatusText = (isApproved) => {
  const status = isApproved ? 'Reviewed' : 'Pending';
  const color = isApproved ? '#4caf50' : '#f44336';
  
  return (
    <Typography
      variant="body2"
      sx={{
        color: color,
        fontWeight: 600,
        fontSize: '0.875rem',
        textAlign: 'center'
      }}
    >
      {status}
    </Typography>
  );
};
  const fetchInvoiceData = async (clientId,invoiceId) => {
    
    try {
      console.log(clientId.join(','))
      setGeneratingPDF(true);
      
      const data = {
        clientId: clientId.join(','),
        year: selectedYear,
        month: monthOptions.findIndex(m => m === selectedMonth) + 1,
        invoice_id : invoiceId
      };
      
      getInvoiceData(data, (response) => {
        if (response.status === 200) {
          if(response.data[0].message=='No client invoice data found'){
            setErrorStatus(true);
            return
          }                
          setCurrentInvoiceData(response.data);
          setInvoicePreviewOpen(true);
        }
      });
    } catch (error) {
      console.error('Error fetching invoice data:', error);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Function to download PDF
  const handleDownloadPDF = () => {
    if (currentInvoiceData) {
      const formattedInvoiceNumber =
        currentInvoiceData?.[0]?.invoice?.formatted_invoice_number ||
        currentInvoiceData?.[0]?.invoice?.number ||
        '';
      let invoiceNumber = formattedInvoiceNumber
        ? `Invoice_${selectedMonth}_${formattedInvoiceNumber}`
        : 'Invoice_' + selectedMonth + '_' + `${Date.now().toString().slice(-5)}`
      const cleanName = currentClientName
      savePDF(currentInvoiceData, invoiceNumber);
    }
  };

  // Function to handle both preview and download
  const handleInvoiceAction = (row) => {
    
    const clientName = row.displayname?.[0] || 'Client';
    setCurrentClientName(clientName);
    fetchInvoiceData(row.ClientId,row.InvoiceId);
  };

  const openSendEmailDialog = (row) => {
    
    const clientId = Array.isArray(row.ClientId) ? row.ClientId.join(',') : row.ClientId;
    const invoiceId = Array.isArray(row.InvoiceId) ? row.InvoiceId[0] : row.InvoiceId;
    const invoiceNumber = row.invoiceNumber || row.invoiceNumber || row.invoiceNumber || '';
    const invoice_id =  Array.isArray(row.invoice_id) ? row.invoice_id[0] : row.invoice_id;
    const billing_id = Array.isArray(row.billing_id) ? row.billing_id[0] : row.billing_id;
    setSendEmailContext({
      clientId,
      invoiceId,
      invoiceNumber,
      invoice_id,
      billing_id
    });
    setUpdateReason('');
    setSendEmailOpen(true);
  };

//   const renderApprovalControls = (actionTarget) => {
//   if (!actionTarget) {
//     return null;
//   }

//   const isOpsApproved = toApprovalFlag(actionTarget.isOpsApproved) === 1;
//   const isAccountsApproved = toApprovalFlag(actionTarget.isAccountsApproved) === 1;
//   const isBillingIdMissing = !hasValue(actionTarget.billingId);
//   const isOpsApprovalUpdating = approvalLoadingId === getApprovalLoadingKey(actionTarget.billingId, 'IsOpsApproved');
//   const isAccountsApprovalUpdating = approvalLoadingId === getApprovalLoadingKey(actionTarget.billingId, 'IsAccountsApproved');

//   const isMainUser = currentUserId === APPROVAL_USER_IDS.MAIN;
//   const isOpsUser = currentUserId === APPROVAL_USER_IDS.OPS;
//   const isAccountsUser = currentUserId === APPROVAL_USER_IDS.ACCOUNTS;

//   const canEditOps = isOpsUser || isMainUser;
//   const canEditAccounts = isAccountsUser || isMainUser;
//   const isOpsDisabled = !canEditOps || isBillingIdMissing || isOpsApprovalUpdating;
//   const isAccountsDisabled = !canEditAccounts || isBillingIdMissing || isAccountsApprovalUpdating || (isOpsUser && !isOpsApproved);

//   const handleOpsClick = () => {
//     if (!isOpsDisabled && canEditOps) {
//       handleApprovalChange(actionTarget, 'IsOpsApproved', !isOpsApproved);
//     }
//   };

//   const handleAccountsClick = () => {
//     if (!isAccountsDisabled && canEditAccounts) {
//       handleApprovalChange(actionTarget, 'IsAccountsApproved', !isAccountsApproved);
//     }
//   };

//  return (
//   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
//     <Button
//       variant={isOpsApproved ? 'contained' : 'outlined'}
//       size="small"
//       onClick={handleOpsClick}
//       disabled={isOpsDisabled}
//       startIcon={isOpsApproved ? <CheckIcon /> : <CloseIcon />}
//       sx={{
//         //borderRadius: '25px',
//         textTransform: 'none',
//         fontWeight: 600,
//         fontSize: '0.7rem',
//         px: 1.5,
//         ...(isOpsApproved && {
//           backgroundColor: '#4caf50',
//           '&:hover': { backgroundColor: '#388e3c' }
//         }),
//         ...(!isOpsApproved && {
//           borderColor: '#f44336',
//           color: '#f44336',
//           '&:hover': { 
//             borderColor: '#d32f2f',
//             backgroundColor: 'rgba(244, 67, 54, 0.04)'
//           }
//         })
//       }}
//     >
//       OPS
//     </Button>
    
//     <Button
//       variant={isAccountsApproved ? 'contained' : 'outlined'}
//       size="small"
//       onClick={handleAccountsClick}
//       disabled={isAccountsDisabled}
//       startIcon={isAccountsApproved ? <CheckIcon /> : <CloseIcon />}
//       sx={{
//         //borderRadius: '25px',
//         textTransform: 'none',
//         fontWeight: 600,
//         fontSize: '0.7rem',
//         px: 1.5,
//         ...(isAccountsApproved && {
//           backgroundColor: '#4caf50',
//           '&:hover': { backgroundColor: '#388e3c' }
//         }),
//         ...(!isAccountsApproved && {
//           borderColor: '#f44336',
//           color: '#f44336',
//           '&:hover': { 
//             borderColor: '#d32f2f',
//             backgroundColor: 'rgba(244, 67, 54, 0.04)'
//           }
//         })
//       }}
//     >
//       ACCOUNTS
//     </Button>
//   </Box>
// );
// };

//   const renderApprovalControls = (actionTarget) => {
//   if (!actionTarget) {
//     return null;
//   }

//   const isOpsApproved = toApprovalFlag(actionTarget.isOpsApproved) === 1;
//   const isAccountsApproved = toApprovalFlag(actionTarget.isAccountsApproved) === 1;
//   const isBillingIdMissing = !hasValue(actionTarget.billingId);
//   const isOpsApprovalUpdating = approvalLoadingId === getApprovalLoadingKey(actionTarget.billingId, 'IsOpsApproved');
//   const isAccountsApprovalUpdating = approvalLoadingId === getApprovalLoadingKey(actionTarget.billingId, 'IsAccountsApproved');

//   // Determine if user is Main, Ops, or Accounts
//   const isMainUser = currentUserId === APPROVAL_USER_IDS.MAIN;
//   const isOpsUser = currentUserId === APPROVAL_USER_IDS.OPS;
//   const isAccountsUser = currentUserId === APPROVAL_USER_IDS.ACCOUNTS;

//   // Define permissions
//   const canEditOps = isOpsUser || isMainUser;
//   const canEditAccounts = isAccountsUser || isMainUser;
//   const isOpsReadOnly = !canEditOps || isBillingIdMissing || isOpsApprovalUpdating;
//   const isAccountsReadOnly = !canEditAccounts || isBillingIdMissing || isAccountsApprovalUpdating || (isOpsUser && !isOpsApproved);

//   // For Main user, show all toggles as read-only
//   if (isMainUser) {
//     return (
//       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', pointerEvents: 'auto' }}>
//         <ApprovalSwitchControl
//           label="OPS Approval"
//           checked={isOpsApproved}
//           disabled={true}
//           onChange={undefined}
//         />
//         <ApprovalSwitchControl
//           label="Accounts Approval"
//           checked={isAccountsApproved}
//           disabled={true}
//           onChange={undefined}
//         />
//       </Box>
//     );
//   }

//   // For Ops user - Ops toggle enabled, Accounts toggle read-only
//   if (isOpsUser) {
//     return (
//       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', pointerEvents: 'auto' }}>
//         <ApprovalSwitchControl
//           label="OPS Approval"
//           checked={isOpsApproved}
//           disabled={isOpsReadOnly}
//           onChange={(event) => handleApprovalChange(actionTarget, 'IsOpsApproved', event.target.checked)}
//         />
//         <ApprovalSwitchControl
//           label="Accounts Approval"
//           checked={isAccountsApproved}
//           disabled={true}
//           onChange={undefined}
//         />
//       </Box>
//     );
//   }

//   // For Accounts user - Accounts toggle enabled, Ops toggle read-only
//   if (isAccountsUser) {
//     return (
//       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', pointerEvents: 'auto' }}>
//         <ApprovalSwitchControl
//           label="OPS Approval"
//           checked={isOpsApproved}
//           disabled={true}
//           onChange={undefined}
//         />
//         <ApprovalSwitchControl
//           label="Accounts Approval"
//           checked={isAccountsApproved}
//           disabled={isAccountsReadOnly}
//           onChange={(event) => handleApprovalChange(actionTarget, 'IsAccountsApproved', event.target.checked)}
//         />
//       </Box>
//     );
//   }

//   // Fallback for any other user types - show all as read-only
//   return (
//     <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', pointerEvents: 'auto' }}>
//       <ApprovalSwitchControl
//         label="OPS Approval"
//         checked={isOpsApproved}
//         disabled={true}
//         onChange={undefined}
//       />
//       <ApprovalSwitchControl
//         label="Accounts Approval"
//         checked={isAccountsApproved}
//         disabled={true}
//         onChange={undefined}
//       />
//     </Box>
//   );
// };

  
  const renderPreviewActionButton = (billingRowData, actionTarget) => {
    if (!actionTarget) {
      return null;
    }

    return (
      <IconButton
        color="primary"
        size="small"
        onClick={() => {
          if (
            billingRowData.ClientId &&
            billingRowData.ClientId.length > 0
          ) {
            const fallbackInvoiceIdValue = Array.isArray(billingRowData.invoice_id)
              ? billingRowData.invoice_id[0]
              : billingRowData.invoice_id;
            const invoiceIdValue = hasValue(actionTarget.invoiceDatabaseId)
              ? actionTarget.invoiceDatabaseId
              : fallbackInvoiceIdValue;

            handleInvoiceAction({
              ClientId: billingRowData.ClientId,
              displayname: billingRowData.displayname?.[0],
              no_of_cameras: billingRowData.no_of_cameras?.[0],
              Pricing: billingRowData.Pricing?.[0],
              Credits: billingRowData.Credits?.[0],
              InvoiceId: invoiceIdValue
            });
          }
        }}
        disabled={generatingPDF}
        sx={{
          backgroundColor: "primary.main",
          color: "#fff",
          pointerEvents: 'auto',
          "&:hover": {
            backgroundColor: "primary.dark"
          }
        }}
      >
        <PictureAsPdfIcon />
      </IconButton>
    );
  };

  const renderShareActionButton = (billingRowData, actionTarget) => {
    
    if (!actionTarget) {
      return null;
    }

    const canSend = isInvoiceActionEnabled(actionTarget);

    return (
      <IconButton
        color="primary"
        size="small"
        onClick={() => {
          if (
            billingRowData.ClientId &&
            billingRowData.ClientId.length > 0 &&
            canSend
          ) {
            openSendEmailDialog({
              ClientId: billingRowData.ClientId,
              InvoiceId: actionTarget.invoiceId,
              invoiceNumber: billingRowData.invoice_no,
              invoice_id: actionTarget.invoiceDatabaseId,
              billing_id : billingRowData.billing_id
            });
          }
        }}
        disabled={generatingPDF || !canSend}
        sx={{
          backgroundColor: "primary.main",
          color: "#fff",
          pointerEvents: 'auto',
          "&:hover": {
            backgroundColor: "primary.dark"
          }
        }}
      >
        <SendIcon />
      </IconButton>
    );
  };

  const handleSendEmailSave = () => {
    
    console.log(sendEmailContext)
    if (!sendEmailContext) {
      return;
    }
    if (!updateReason.trim()) {
      return;
    }
    const monthIndex = monthOptions.findIndex(m => m === selectedMonth) + 1;
    setSendingEmail(true);
    const updateData = {
      BillingId: sendEmailContext.billing_id, 
      Year: selectedYear,
      Month: monthIndex,
      UpdateReason: updateReason.trim()
    };

    updateEditReason(updateData,(response)=>{
       if (response.status === 200) {
        const invoiceId = sendEmailContext.invoice_id || 0;

         const emailData = {
          month: monthIndex,
          year: selectedYear,
          invoice_id: invoiceId,
          clientId: sendEmailContext.clientId,
         // ...(invoiceId !== 0 && { clientId: sendEmailContext.clientId }),
           ...(invoiceId === 0 && { mail_type: 'manual' }),
        };

        //console.log('emailData', emailData)
        
          sendInvoiceEmail(emailData, (response) => {
          if(response.status===200){
              setMessage('Email Sent successfully!');
              setAlertType('success');
              setStatusBase(true);
          }
          else{
              setMessage('Fail To Sent Mail');
              setAlertType('error');
              setStatusBase(true);
          }
          setSendingEmail(false);
          setSendEmailOpen(false);
        });
        return;
      }
      setSendingEmail(false);
    })
  };

  useEffect(() => {
        if (errorStatus) {
            const timer = setTimeout(() => {
             setErrorStatus(false)
            }, 2000); // 2 seconds

            return () => clearTimeout(timer); 
        }
    }, [errorStatus]);

      useEffect(() => {
        if (status && message) {
            const timer = setTimeout(() => {
                setMessage('');
                setStatusBase(false);
            }, 2000); // 2 seconds

            return () => clearTimeout(timer);
        }
    }, [status, message]);
  return (
    <>
      <Page title="Invoice Report">
        <Card variant="outlined" sx={{ p: 1 }}>
          <CardContent>
            <Typography variant="h4">Invoice Report</Typography>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={2}>
                <FormControl fullWidth error={!!yearError}>
                  <InputLabel>Select Year</InputLabel>
                  <Select
                    value={selectedYear}
                    onChange={(e) => {
                      setSelectedYear(e.target.value);
                      setYearError('');
                    }}
                    label="Select Year"
                  >
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                  {yearError && <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5, display: 'block' }}>
                    {yearError}
                  </Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth error={!!monthsError}>
                  <InputLabel>Select Month</InputLabel>
                  <Select
                    value={selectedMonth}
                    onChange={(e) => {
                      setSelectedMonth(e.target.value);
                      setMonthsError('');
                    }}
                    label="Select Month"
                  >
                    {monthOptions.map((month, index) => {
                      const currentMonthIndex = new Date().getMonth();
                      //const isDisabled = index < currentMonthIndex;

                      return (
                        <MenuItem
                          key={month}
                          value={month}
                          //disabled={isDisabled}
                        >
                          {month}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {monthsError && (
                    <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5, display: 'block' }}>
                      {monthsError}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            {(status && message) && (
                  <Stack direction="row" sx={{ paddingTop: 2 }}>
                      <Alert
                          severity={alertType}
                          sx={{
                              width: '30%',
                              opacity: 0,
                              animation: 'fadeInOut 2.5s ease-in-out',
                              '@keyframes fadeInOut': {
                                  '0%': { opacity: 0 },
                                  '20%': { opacity: 1 },
                                  '80%': { opacity: 1 },
                                  '100%': { opacity: 0 },
                              }
                          }}
                      >
                          {message}
                      </Alert>
                  </Stack>
              )}

            {(errorStatus) && (
                <Stack direction="row" sx={{ paddingTop: 2 }}>
                    <Alert 
                    severity='error'
                    sx={{
                        width: '30%',
                        opacity: 0,
                        animation: 'fadeInOut 2.5s ease-in-out',
                        '@keyframes fadeInOut': {
                        '0%': { opacity: 0 },
                        '20%': { opacity: 1 },
                        '80%': { opacity: 1 },
                        '100%': { opacity: 0 },
                        }
                    }}
                    >
                    No data available to preview
                    </Alert>
                </Stack>
          )}


            <Grid container spacing={0} alignItems="center" sx={{ marginTop: "40px", width:'100%', maxWidth: '100%', marginLeft:'0px', overflowX: 'hidden' }}>
              <Scrollbar
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'hidden',
                  '& .simplebar-content-wrapper': {
                    overflowX: 'hidden !important'
                  },
                  '& .simplebar-track.simplebar-horizontal': {
                    display: 'none'
                  }
                }}
              >
                <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                  <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%', minWidth: '100%' }}>
                    {/* <TableHead sx={{ background: "#f3f3f3" }}>
                      <TableRow sx={{ background: '#f3f3f3' }}>
                        <TableCell align="center" sx={{ width: 70 }}>S.No</TableCell>
                        <TableCell colSpan={9} sx={{ p: 0 }}>
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: billingGridTemplateColumns,
                            width: '100%',
                            minHeight: 72
                          }}>
                            <Box sx={getBillingHeaderCellSx('left')}>Client Name</Box>
                            <Box sx={getBillingHeaderCellSx('left')}>Locations</Box>
                            <Box sx={getBillingHeaderCellSx()}>No of Cameras</Box>
                            <Box sx={getBillingHeaderCellSx()}>No of Views</Box>
                            <Box sx={getBillingHeaderCellSx()}>Views Considered For Billing</Box>

                            <Box sx={getBillingHeaderCellSx()}>Total Price($)</Box>
                            <Box sx={getBillingHeaderCellSx()}>Credits($)</Box>
                            <Box sx={getBillingHeaderCellSx()}>Outstanding($)</Box>
                            <Box sx={getBillingHeaderCellSx('left')}>Action</Box>
                          </Box>
                        </TableCell>
                    
                      </TableRow>
                    </TableHead> */}

                    <TableHead sx={{ background: "#f3f3f3" }}>
  <TableRow sx={{ background: '#f3f3f3' }}>
    <TableCell align="center" sx={{ width: serialColumnWidth, px: 0.5 }}>S.No</TableCell>
    <TableCell colSpan={14} sx={{ p: 0 }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: billingGridTemplateColumns,
        width: '100%',
        minHeight: 72
      }}>
        <Box sx={getBillingHeaderCellSx('left')}>Client Name</Box>
        <Box sx={getBillingHeaderCellSx('left')}>Locations</Box>
        <Box sx={getBillingHeaderCellSx()}>No of Cameras</Box>
        <Box sx={getBillingHeaderCellSx()}>No of Views</Box>
        <Box sx={getBillingHeaderCellSx()}>Views for Billing</Box>
        <Box sx={getBillingHeaderCellSx()}>Total Price($)</Box>
        <Box sx={getBillingHeaderCellSx()}>Storage($)</Box>
        <Box sx={getBillingHeaderCellSx()}>Credits($)</Box>
        <Box sx={previousBalanceHeaderSx}>
          <Box component="span">Previous Balance</Box>
          <Box component="span">Due($)</Box>
        </Box>
        <Box sx={getBillingHeaderCellSx()}>Edit</Box>
        <Box sx={getBillingHeaderCellSx()}>Preview</Box>
        <Box sx={getBillingHeaderCellSx()}>Share</Box>
        <Box sx={getBillingHeaderCellSx()}>Ops Status</Box>
        <Box sx={getBillingHeaderCellSx()}>Accounts Status</Box>
      </Box>
    </TableCell>
  </TableRow>
</TableHead>              
<TableBody>
  {dashboardData.length > 0 ? (
    <>
    {dashboardData.map((rowData, rowIndex) => {
      const displayNames = getStackedValues(rowData, 'displayname');
      const locations = getStackedValues(rowData, 'Location');
      const camerasList = getStackedValues(rowData, 'no_of_cameras');
      const viewsList = getStackedValues(rowData, 'no_of_views');
      const viewsConsideredList = getStackedValues(rowData, 'ViewsConsidered');
      const totalPricing = getStackedValues(rowData, 'total_pricing');
      const storageAmountList = getStackedValues(rowData, 'StorageAmount');
      const creditsList = getStackedValues(rowData, 'Credits');
      const outstandingList = getStackedValues(rowData, 'OutstandingAmount');
      const editTargets = getEditTargets(rowData, rowIndex);
      const lineCount = rowData._billingLineCount || 1;
      const hasLocations = locations.some(hasValue);
      const currentYear = new Date().getFullYear();
      const primaryEditTarget = editTargets[0];
      const isPrimaryEditDisabled = selectedYear && currentYear > Number(selectedYear);

      // Use a stable target for grouped action payloads; the overlay handles visual centering.
      const middleIndex = Math.floor(lineCount / 2);
      const groupedActionTarget = editTargets[middleIndex] || primaryEditTarget;

      return (
        <TableRow key={`row-${rowIndex}`} sx={billingTableRowSx}>
          <TableCell align="center" sx={{ px: 0.5 }}>{rowIndex + 1}</TableCell>
          <TableCell colSpan={14} sx={{ p: 0, position: 'relative' }}>
            {Array.from({ length: lineCount }, (_, idx) => {
              const isEditing = editingId === `${rowIndex}-${idx}`;
              const invoiceId = getInvoiceIdAt(rowData, idx);
              const hasInvoiceId = hasValue(invoiceId);
              const currentEditTarget = editTargets[idx];

              return (
                <Box
                  key={`${rowIndex}-billing-line-${idx}`}
                  sx={getBillingLineRowSx(rowData, idx, lineCount)}
                >
                  <Box sx={getBillingLineCellSx('left')}>{displayCellValue(displayNames[idx])}</Box>
                  <Box sx={getBillingLineCellSx('left')}>{hasLocations ? displayCellValue(locations[idx]) : ''}</Box>
                  <Box sx={getBillingLineCellSx()}>{displayCellValue(camerasList[idx])}</Box>
                  <Box sx={getBillingLineCellSx()}>{displayCellValue(viewsList[idx])}</Box>
                  <Box sx={getBillingLineCellSx()}>{displayCellValue(viewsConsideredList[idx])}</Box>
                  <Box sx={getBillingLineCellSx()}>{displayCellValue(totalPricing[idx])}</Box>
                  <Box sx={getBillingLineCellSx()}>{displayCellValue(storageAmountList[idx])}</Box>
                  <Box sx={getBillingLineCellSx()}>                   
                    {displayCellValue(creditsList[idx])}
                  </Box>
                  <Box sx={getBillingLineCellSx()}>
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        disabled={loading}
                        autoFocus
                        sx={{ width: '100%', maxWidth: 120 }}
                      />
                    ) : (
                      displayCellValue(outstandingList[idx])
                    )}
                  </Box>             
                  <Box sx={getBillingLineCellSx()}>
                    {isEditing ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 1,
                        minWidth: 0,
                        width: '100%',
                        //mt: 2,  // Add margin-top to push everything down
                        //pt: 1   // Add padding-top for extra spacing
                      }}>
                        {/* Save/Cancel Buttons */}
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mb: 0.5 }}>
                          <IconButton
                            size="medium"
                            color="primary"
                            onClick={() => {
                              const currentClientData = {
                                rowIndex,
                                valueIndex: currentEditTarget?.valueIndex,
                                clientId: currentEditTarget?.clientId,
                                displayname: currentEditTarget?.displayname,
                                OutstandingAmount: currentEditTarget?.OutstandingAmount,
                                originalOutstanding: currentEditTarget?.OutstandingAmount,
                                Credits: currentEditTarget?.Credits,
                                originalCredits: currentEditTarget?.Credits,
                                //AdditionalAmount: currentEditTarget?.AdditionalAmount,
                                //AdditionalAmountType: currentEditTarget?.AdditionalAmountType,
                                invoiceId: currentEditTarget?.invoiceId,
                                hasInvoiceId: currentEditTarget?.hasInvoiceId
                              };
                              handleSave(currentClientData);
                            }}
                            disabled={loading}
                          >
                            <SaveIcon fontSize="medium" />
                          </IconButton>
                          <IconButton
                            size="medium"
                            color="error"
                            onClick={handleCancel}
                            disabled={loading}
                          >
                            <CancelIcon fontSize="medium" />
                          </IconButton>
                        </Box>

                      
                        {/* <TextField
                          size="small"
                          type="number"
                          label="Additional Amount"
                          value={editAdditionalAmount}
                          onChange={(e) => setEditAdditionalAmount(e.target.value)}
                          disabled={loading}
                          variant="outlined"
                          sx={{ 
                            width: '100%',
                            mt: 0.5,  // Add small margin top
                            '& .MuiInputBase-root': {
                              fontSize: '0.75rem',
                              height: '32px'
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.7rem',
                              transform: 'translate(10px, 8px) scale(1)'
                            },
                            '& .MuiInputLabel-shrink': {
                              transform: 'translate(10px, -4px) scale(0.75)'
                            },
                            '& input': {
                              padding: '6px 10px',
                              fontSize: '0.75rem'
                            }
                          }}
                        />

                        
                        <TextField
                          size="small"
                          label="Amount Type"
                          value={editAdditionalAmountType}
                          onChange={(e) => setEditAdditionalAmountType(e.target.value)}
                          disabled={loading}
                          variant="outlined"
                          placeholder="Optional"
                          sx={{ 
                            width: '100%',
                            mb: 0.5,  // Add small margin bottom
                            '& .MuiInputBase-root': {
                              fontSize: '0.75rem',
                              height: '32px'
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.7rem',
                              transform: 'translate(10px, 8px) scale(1)'
                            },
                            '& .MuiInputLabel-shrink': {
                              transform: 'translate(10px, -4px) scale(0.75)'
                            },
                            '& input': {
                              padding: '6px 10px',
                              fontSize: '0.75rem'
                            }
                          }}
                        /> */}
                      </Box>
                    ) : (
                    
                      <IconButton
                    size="small"
                    sx={{
                      backgroundColor: "primary.main",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    }}
                    onClick={() =>
                      handleEdit({
                        uniqueId: currentEditTarget?.uniqueId,
                        ClientId: currentEditTarget?.clientId,
                        displayname: currentEditTarget?.displayname,
                        OutstandingAmount: currentEditTarget?.OutstandingAmount,
                        Credits: currentEditTarget?.Credits,
                        //AdditionalAmount: currentEditTarget?.AdditionalAmount,
                        //AdditionalAmountType: currentEditTarget?.AdditionalAmountType,
                        InvoiceId: currentEditTarget?.invoiceId
                      })
                    }
                    disabled={isPrimaryEditDisabled || !hasValue(currentEditTarget?.clientId)}
                  >
                    <EditIcon fontSize="small"/>
                  </IconButton>
                    )}
                  </Box>
                  {/* Preview Column - only show once at the middle row for grouped rows */}
                  <Box sx={getBillingLineCellSx()}>
                    {lineCount === 1 && renderPreviewActionButton(rowData, currentEditTarget)}
                  </Box>

                  {/* Share Column - only show once at the middle row for grouped rows */}
                  <Box sx={getBillingLineCellSx()}>
                    {lineCount === 1 && renderShareActionButton(rowData, currentEditTarget)}
                  </Box>

                  {/* Ops Status Column - only show once at the middle row for grouped rows */}
                  <Box sx={getBillingLineCellSx()}>
                    {lineCount === 1 && currentEditTarget && (
                      renderOpsApprovalControl(currentEditTarget)
                    )}
                  </Box>

                  {/* Accounts Status Column - only show once at the middle row for grouped rows */}
                  <Box sx={getBillingLineCellSx()}>
                    {lineCount === 1 && currentEditTarget && (
                      renderAccountsApprovalControl(currentEditTarget)
                    )}
                  </Box>
                </Box>
              );
            })}
           {lineCount > 1 && groupedActionTarget && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  gridTemplateColumns: billingGridTemplateColumns,
                  gridTemplateRows: '1fr',
                  pointerEvents: 'none'
                }}
              >
                <Box sx={getGroupedActionCellSx('11 / 12')}>
                  {renderPreviewActionButton(rowData, groupedActionTarget)}
                </Box>
                <Box sx={getGroupedActionCellSx('12 / 13')}>
                  {renderShareActionButton(rowData, groupedActionTarget)}
                </Box>
                <Box sx={getGroupedActionCellSx('13 / 14', 'center')}>
                  {renderOpsApprovalControl(groupedActionTarget)}
                </Box>
                <Box sx={getGroupedActionCellSx('14 / 15', 'center')}>
                  {renderAccountsApprovalControl(groupedActionTarget)}
                </Box>
              </Box>
            )}
          </TableCell>
        </TableRow>
      );
    })}
    <TableRow sx={billingTableRowSx}>
      <TableCell colSpan={15} sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: grandTotalGridTemplateColumns,
            width: '100%',
            minHeight: 67
          }}
        >
          <Box sx={{ ...getGrandTotalCellSx(), gridColumn: '1 / 4' }}>
            Grand Total
          </Box>
          <Box sx={getGrandTotalCellSx()}>
            {formatGrandTotalValue(grandTotals.cameras)}
          </Box>
          <Box sx={getGrandTotalCellSx()}>
            {formatGrandTotalValue(grandTotals.views)}
          </Box>
          <Box sx={getGrandTotalCellSx()}>
            {formatGrandTotalValue(grandTotals.viewsConsidered)}
          </Box>
          <Box sx={getGrandTotalCellSx()}>
            {formatGrandTotalValue(grandTotals.totalPricing)}
          </Box>
          <Box sx={getGrandTotalCellSx()}>
            {formatGrandTotalValue(grandTotals.storage)}
          </Box>
          <Box sx={getGrandTotalCellSx()}>
            {formatGrandTotalValue(grandTotals.credits)}
          </Box>
          <Box sx={getGrandTotalCellSx()}>
            {formatGrandTotalValue(grandTotals.outstanding)}
          </Box>
          {Array.from({ length: 5 }, (_, index) => (
            <Box key={`grand-total-empty-${index}`} sx={getGrandTotalCellSx()} />
          ))}
        </Box>
      </TableCell>
    </TableRow>
    </>
  ) : (
    <TableRow>
      <TableCell colSpan={15} align="center">No Data</TableCell>
    </TableRow>
  )}
</TableBody>


                  </Table>
                </TableContainer>
              </Scrollbar>
            </Grid>
          </CardContent>
        </Card>

        {/* PDF Preview Dialog */}
        <Dialog
          open={invoicePreviewOpen}
          onClose={() => setInvoicePreviewOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Invoice Preview
            <IconButton
              onClick={() => handleDownloadPDF()}
              sx={{ position: 'absolute', right: 70, top: 8 }}
              disabled={!currentInvoiceData}
            >
              {/* <DownloadIcon /> */}
            </IconButton>
            <IconButton
              onClick={() => setInvoicePreviewOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CancelIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                height: '70vh',
                width: '100%',
                overflow: isFirefox ? 'hidden' : 'visible',
              }}
            >
              {currentInvoiceData && (
                <PDFViewer
                  width="100%"
                  height="100%"
                  showToolbar={false}
                  style={{
                    border: 'none',
                    height: isFirefox ? `calc(100% + ${FIREFOX_PDF_TOOLBAR_HEIGHT}px)` : '100%',
                    transform: isFirefox ? `translateY(-${FIREFOX_PDF_TOOLBAR_HEIGHT}px)` : 'none',
                    width: '100%',
                  }}
                >
                  <InvoicePDF invoiceData={currentInvoiceData} />
                </PDFViewer>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleDownloadPDF} 
              startIcon={<DownloadIcon />}
              disabled={!currentInvoiceData}
              variant="contained"
            >
              Download PDF
            </Button>
            <Button 
              onClick={() => setInvoicePreviewOpen(false)} 
              variant="outlined"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Send Email Dialog */}
        <Dialog
          open={sendEmailOpen}
          onClose={() => setSendEmailOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Send Email</DialogTitle>
          <DialogContent dividers>
            <TextField
              label="Update Reason"
              value={updateReason}
              onChange={(e) => setUpdateReason(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              autoFocus
              sx={(theme) => ({
                '& .MuiInputLabel-root': {
                  color: theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white,
                },
                '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                  color: theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white,
                },
              })}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleSendEmailSave}
              variant="contained"
              disabled={sendingEmail || !updateReason.trim()}
              
              >
              Send
            </Button>
            <Button
              onClick={() => setSendEmailOpen(false)}
              variant="outlined"
              disabled={sendingEmail}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Page>
    </>
  );
}
